import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import type { SelectOption } from '@/shared/ui/select/types';
import type { DeploymentFormChange, DeploymentFormState } from './deployment-settings-types';

export type DeploymentDefaultsFieldsProps = {
  values: DeploymentFormState;
  providerOptions: SelectOption[];
  onChange: DeploymentFormChange;
};

/** The provider and model a tenant reaches by default. */
export function DeploymentDefaultsFields(props: DeploymentDefaultsFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Defaults</h3>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Default provider"
          options={props.providerOptions}
          value={props.values.defaultProviderId}
          onChange={(event) => {
            props.onChange('defaultProviderId', event.target.value);
          }}
          placeholder="No default provider"
          hint="The provider tenants reach when they have not configured one of their own."
        />
        <Input
          label="Default model"
          value={props.values.defaultModelId}
          onChange={(event) => {
            props.onChange('defaultModelId', event.currentTarget.value);
          }}
          hint="Free text — the exact model name the default provider expects."
          maxLength={200}
          placeholder="gpt-4o-mini"
        />
      </div>

      <Input
        label="Default prompt version"
        value={props.values.defaultPromptVersion}
        onChange={(event) => {
          props.onChange('defaultPromptVersion', event.currentTarget.value);
        }}
        hint="The prompt template version the platform sends by default."
        maxLength={120}
        placeholder="v1"
      />
    </section>
  );
}

export type DeploymentGenerationFieldsProps = {
  values: DeploymentFormState;
  onChange: DeploymentFormChange;
};

/** How the platform asks its default provider to generate. */
export function DeploymentGenerationFields(props: DeploymentGenerationFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Generation</h3>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Max output tokens"
          type="number"
          value={props.values.maxOutputTokens}
          onChange={(event) => {
            props.onChange('maxOutputTokens', event.currentTarget.value);
          }}
          min={1}
          placeholder="Leave empty for the provider default"
        />
        <Input
          label="Temperature"
          type="number"
          value={props.values.temperature}
          onChange={(event) => {
            props.onChange('temperature', event.currentTarget.value);
          }}
          min={0}
          max={2}
          step={0.1}
          placeholder="Leave empty for the provider default"
        />
      </div>
    </section>
  );
}

export type DeploymentAllowancesFieldsProps = {
  values: DeploymentFormState;
  onChange: DeploymentFormChange;
};

/** What the platform may spend, and what a tenant inherits. */
export function DeploymentAllowancesFields(props: DeploymentAllowancesFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Allowances</h3>
      <p className="text-sm text-content-muted">
        Leave a field empty for no limit. The platform-funded allowances are spent by the platform
        on a tenant&apos;s behalf; the tenant defaults are what a tenant inherits.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Platform requests per month"
          type="number"
          value={props.values.platformMonthlyRequestLimit}
          onChange={(event) => {
            props.onChange('platformMonthlyRequestLimit', event.currentTarget.value);
          }}
          min={0}
          placeholder="No limit"
        />
        <Input
          label="Platform tokens per month"
          type="number"
          value={props.values.platformMonthlyTokenLimit}
          onChange={(event) => {
            props.onChange('platformMonthlyTokenLimit', event.currentTarget.value);
          }}
          min={0}
          placeholder="No limit"
        />
        <Input
          label="Tenant default requests per month"
          type="number"
          value={props.values.tenantDefaultRequestLimit}
          onChange={(event) => {
            props.onChange('tenantDefaultRequestLimit', event.currentTarget.value);
          }}
          min={0}
          placeholder="No limit"
        />
        <Input
          label="Tenant default tokens per month"
          type="number"
          value={props.values.tenantDefaultTokenLimit}
          onChange={(event) => {
            props.onChange('tenantDefaultTokenLimit', event.currentTarget.value);
          }}
          min={0}
          placeholder="No limit"
        />
      </div>
    </section>
  );
}

export type DeploymentPolicyFieldsProps = {
  values: DeploymentFormState;
  onChange: DeploymentFormChange;
};

/**
 * The deployment's policy switches.
 *
 * ---------------------------------------------------------------------------
 * THE TWO POLICY TOGGLES
 * ---------------------------------------------------------------------------
 * "Tenants may configure their own provider" and "Platform fallback allowed"
 * both default to OFF at the API, and both are labelled with what off MEANS:
 * a tenant-owned provider spends the tenant's own money, and a platform
 * fallback spends the platform's allowance on the tenant's behalf. Neither is
 * a cosmetic default, so neither is presented as one.
 */
export function DeploymentPolicyFields(props: DeploymentPolicyFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Policy</h3>

      <div className="space-y-4">
        <Switch
          label="AI is enabled for this deployment"
          description="When off, no tenant can use AI at all — not even with a provider of its own."
          checked={props.values.aiEnabled}
          onCheckedChange={(checked) => {
            props.onChange('aiEnabled', checked);
          }}
        />
        <Switch
          label="Tenants may configure their own provider"
          description="Off by default. When off, every tenant uses the platform's provider and the platform's allowance."
          checked={props.values.tenantOwnedProviderAllowed}
          onCheckedChange={(checked) => {
            props.onChange('tenantOwnedProviderAllowed', checked);
          }}
        />
        <Switch
          label="Platform fallback allowed"
          description="Off by default. When off, a tenant with no provider of its own simply has no AI — the platform does not step in and spend the platform's allowance."
          checked={props.values.platformFallbackAllowed}
          onCheckedChange={(checked) => {
            props.onChange('platformFallbackAllowed', checked);
          }}
        />
      </div>
    </section>
  );
}
